import React, { useState } from "react";
import {
  useOrganisationMembers,
  useOrganisationRoles,
} from "../../hooks/useOrganisations";
import { useUsers } from "../../hooks/useUsers";
import DataTableBase from "./dataTableBase";
import {
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Chip,
} from "@mui/material";
import ConfirmDeleteMember from "../Dialogs/ConfirmDeleteMember";
import EditMemberRoleButton from "../Buttons/EditMemberRole";

export default function OrganisationMembersTable(props) {
  const { organisation_id } = props;
  const {
    data: organisationMembers,
    isLoading,
    error,
  } = useOrganisationMembers(organisation_id, { enabled: !!organisation_id });

  const { data: organisationRoles = [] } = useOrganisationRoles(
    organisation_id,
    {
      enabled: !!organisation_id,
    }
  );

  const { data: users = [] } = useUsers();

  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [userID, setUserID] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">Failed to load organisation members</Alert>;
  }

  const organisationMembersColumns = [
    {
      name: "id",
      label: "id",
      options: {
        display: false,
      },
    },
    {
      name: "email",
      label: "Email",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          return (
            <Typography
              onClick={() => {
                window.location.href = "/user/" + tableMeta.rowData[0];
              }}
              sx={{
                "&:hover": {
                  cursor: "pointer",
                  textDecoration: "underline",
                },
              }}
            >
              {
                //find user by id and return email
              }
              {users && users.length
                ? users.filter((user) => user.id === tableMeta.rowData[0])[0]
                  ? users.filter((user) => user.id === tableMeta.rowData[0])[0]
                      .email
                  : ""
                : ""}
            </Typography>
          );
        },
      },
    },
    {
      name: "role",
      label: "Role",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const userId = tableMeta.rowData[0];
          const userRole = organisationRoles.find(
            (role) => role.user_id === userId
          );

          if (!userRole) {
            return <Chip label="View Only" size="small" variant="outlined" />;
          }

          if (userRole.admin_permission) {
            return <Chip label="Administrator" size="small" color="primary" />;
          }

          const permissions = [];
          if (userRole.add_permission) permissions.push("Add");
          if (userRole.edit_permission) permissions.push("Edit");
          if (userRole.delete_permission) permissions.push("Delete");
          if (userRole.transfer_permission) permissions.push("Transfer");

          if (permissions.length === 0) {
            return <Chip label="View Only" size="small" variant="outlined" />;
          }

          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {permissions.map((permission) => (
                <Chip
                  key={permission}
                  label={permission}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              ))}
            </Box>
          );
        },
      },
    },
    {
      name: "options",
      label: "Options",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <EditMemberRoleButton
                organisationId={organisation_id}
                userId={tableMeta.rowData[0]}
              />
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  setConfirmDeleteDialogOpen(true);
                  setUserID(tableMeta.rowData[0]);
                }}
              >
                Remove
              </Button>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <>
      <DataTableBase
        title={"Organisation Members"}
        data={organisationMembers}
        columns={organisationMembersColumns}
        orderby="time_updated"
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selectedItems
      />
      <ConfirmDeleteMember
        open={confirmDeleteDialogOpen}
        handleClose={() => {
          setConfirmDeleteDialogOpen(false);
        }}
        user_id={userID}
        organisation_id={props.organisation_id}
      />
    </>
  );
}
